import { Col, Form, Modal, Row, Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import { IBookingState, IOriginDestinationRequest, IShipperAndAccountDetailRequest, ISubProductList } from '../../../../../model/BookingModel';
import { useBookingStore } from '../../../../../stores/BookingStore';
import { useCommonStore } from '../../../../../stores/CommonStore';
import { useOperationStore } from '../../../../../stores/OperationStore';
import { CASH_ID, TWO_HUNDRED_SUCCESS_CODE, dropDownMapping, getProductDetails, getSubClientList, getTemperatureList, sortBy } from '../../../../../utils/app-common';
import { requiredValidation } from '../../../../../validation/validation';
import LoadingModalComponent from '../../../../CommonComponent/LoadingModalComponent';
import { useManifestContextHook } from './Context/useManifestContextHook';
import RevalidateHook from './hooks/RevalidateHook';
import { ICommonState } from '../../../../../model/CommonModel';
import { IUpdateManifest } from '../../../../../model/OperationModel';
import { useUserContextHook } from '../../../../../hooks/useUserContextHook';
import { getNotificationStyle, warningNotificationStyle } from '../../../../../common/notification';

const initialValue = { productId: '', subProductId: '', temperatureId: '', originCityId: '', destinationCityId: '' };



const clearStore = () => {
	useCommonStore.setState({ subCityList: [], consigneeSubCityList: [] })
	useBookingStore.setState({ subProductList: [], temperatureList: [], originDestination: {} })
};
const EditRowComponent = ({ clientId }: { clientId: number }) => {

	const { promiseInProgress: countrySearchPromise } = usePromiseTracker({ area: 'search-country-area' });
	const { promiseInProgress: originCityFetchProgress } = usePromiseTracker({ area: 'origin-city-fetch-area' })
	const { promiseInProgress: destinationFetchFetchProgress } = usePromiseTracker({ area: 'destination-fetch-area' });
	const { billTypeId } = useManifestContextHook();
	const { employeeDetail } = useUserContextHook() || {};

	const accountDetailList = useBookingStore((state: IBookingState) => state.accountDetailList);
	const GetSubProductByIdAction = useBookingStore((state: IBookingState) => state?.GetSubProductByIdAction);
	const GetTemperatureByIdAction = useBookingStore((state: IBookingState) => state?.GetTemperatureByIdAction);
	const GetOriginDestinationCountryCityAction = useBookingStore(state => state?.GetOriginDestinationCountryCityAction);
	const { getFieldValue, setFieldsValue, setFieldValue } = Form.useFormInstance();
	const temperatureList = useBookingStore((state: IBookingState) => state?.temperatureList);
	const subProductList = useBookingStore((state: IBookingState) => state.subProductList);
	const allProductList = useBookingStore((state: IBookingState) => state?.productList);
	const allSubProductList = useBookingStore((state: IBookingState) => state?.subProductList);
	const allTemperatureList = useBookingStore((state: IBookingState) => state?.temperatureList);
	const originDestination = useBookingStore(state => state?.originDestination);
	const consigneeCountryCity = useBookingStore((state: IBookingState) => state?.consigneeCountryCity ?? [{ consigneeDtoList: [] }]);
	const GetSubCityListAction = useCommonStore((state: ICommonState) => state?.GetSubCityListAction);
	const GetConsigneeSubCityListAction = useCommonStore((state: ICommonState) => state?.GetConsigneeSubCityListAction);
	const GetConsigneeDetailsByShipperAndAccountDetailsAction = useBookingStore((state) => state?.GetConsigneeDetailsByShipperAndAccountDetailsAction);
	const manifestationList = useOperationStore((state) => state?.manifestationList);
	const billType = ![CASH_ID].includes(billTypeId!);
	const productListMemo = billType ? dropDownMapping(accountDetailList, 'productId', 'productName') :
		dropDownMapping(allProductList, 'id', 'name');
	const subProductListMemo = billType ? subProductList : dropDownMapping(allSubProductList, 'id', 'productName');
	const temperatureListMemo = billType ? temperatureList : dropDownMapping(allTemperatureList, 'id', 'name');
	const originListMemo = useMemo(() => dropDownMapping(originDestination?.city, 'id', 'name'), [originDestination]);
	const destinationListMemo = useMemo(() => dropDownMapping(consigneeCountryCity.city, 'id', 'name'), [consigneeCountryCity]);
	const subCityList = useCommonStore(
		(state: ICommonState) => state?.subCityList
	);
	const consigneeSubCityList = useCommonStore(
		(state: ICommonState) => state?.consigneeSubCityList
	);
	const destinationSubCityListMemo = useMemo(
		() => dropDownMapping(consigneeSubCityList, "id", "name").sort((a, b) => sortBy(a, b)),
		[consigneeSubCityList]
	);
	const subCityListMemo = useMemo(
		() => dropDownMapping(subCityList, "id", "name").sort((a, b) => sortBy(a, b)),
		[subCityList]
	);

	useEffect(() => {
		if (manifestationList?.originCityId && originListMemo?.some((item) => `${item?.id}` === `${manifestationList.originCityId}`)) {
			setFieldValue('originCityId', manifestationList.originCityId);
		} else {
			setFieldValue('originCityId', "");
		}

	}, [originListMemo, manifestationList?.originCityId, setFieldValue]);

	useEffect(() => {
		if (manifestationList?.destinationCityId && destinationListMemo?.some((item) => `${item?.id}` === `${manifestationList.destinationCityId}`)) {
			setFieldValue('destinationCityId', manifestationList.destinationCityId);
		} else {
			setFieldValue('destinationCityId', "");
		}
	}, [destinationListMemo, manifestationList?.destinationCityId, setFieldValue]);

	useEffect(() => {
		if (manifestationList?.originOdaCityId && subCityListMemo?.some((item) => `${item?.id}` === `${manifestationList.originOdaCityId}`)) {
			setFieldValue('originOdaCityId', manifestationList.originOdaCityId);
		} else {
			setFieldValue('originOdaCityId', "");
		}
	}, [subCityListMemo, manifestationList?.originOdaCityId, setFieldValue]);

	useEffect(() => {
		if (manifestationList?.destinationOdaCityId && destinationSubCityListMemo?.some((item) => `${item?.id}` === `${manifestationList.destinationOdaCityId}`)) {
			setFieldValue('destinationOdaCityId', manifestationList.destinationOdaCityId);
		} else {
			setFieldValue('destinationOdaCityId', "");
		}
	}, [destinationSubCityListMemo, manifestationList?.destinationOdaCityId, setFieldValue]);

	const clearFields = useCallback(() => {
		setFieldsValue({
			subProductId: '',
			temperatureId: '',
		});
		clearStore();
	}, [setFieldsValue]);

	const onProductTypeChange = useCallback((id: number) => {
		clearFields();
		GetSubProductByIdAction(getSubClientList(id, accountDetailList));
	}, [GetSubProductByIdAction, accountDetailList, clearFields])


	const onSubProductTypeChange = useCallback((subProductId: number, list: ISubProductList[] | any[]) => {
		setFieldsValue({
			temperatureId: '',
		})
		GetTemperatureByIdAction(getTemperatureList(subProductId, list));
	}, [GetTemperatureByIdAction, setFieldsValue])

	const onTemperatureChangeHandler = (selectedId: number) => {
		if (!selectedId) {
			return
		}
		const request: IOriginDestinationRequest = { ...requestBody, temperatureId: selectedId }
		trackPromise(GetOriginDestinationCountryCityAction(request), 'search-country-area')
	};

	const requestBody = ({
		subclientId: clientId,
		productId: getFieldValue('productId'),
		subProductId: getFieldValue('subProductId'),
		temperatureId: getFieldValue('temperatureId')
	});



	const onClearTemperatureHandler = () => {
		useBookingStore.setState({ originDestination: {} })
		setFieldsValue({ originOdaCityId: "", destinationCityId: '', destinationOdaCityId: '', originCityId: '' });
	}

	const onChangeHandler = (selectedId: number, key: string) => {
		// Map of actions for resetting fields
		const resetFields = {
			originCityId: { originOdaCityId: null, destinationCityId: '', destinationOdaCityId: null },
			destinationCityId: { destinationOdaCityId: null },
		};

		// Reset fields when no `selectedId`
		if (!selectedId && key in resetFields) {
			setFieldsValue(resetFields[key]);
			return;
		}

		// Actions for specific keys
		switch (key) {
			case 'originCityId': {
				const request: IShipperAndAccountDetailRequest = { ...requestBody, originCityId: selectedId };
				setFieldsValue(resetFields.originCityId);
				trackPromise(GetConsigneeDetailsByShipperAndAccountDetailsAction(request, employeeDetail?.countryId), 'get-consignee-detail');
				trackPromise(GetSubCityListAction(selectedId), 'origin-city-fetch-area');
				break;
			}
			case 'destinationCityId': {
				setFieldsValue(resetFields.destinationCityId);
				trackPromise(GetConsigneeSubCityListAction(selectedId), 'destination-fetch-area');
				break;
			}
			default:
				break;
		}
	};


	return (
		<>
			<Row justify='start' gutter={5}>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						name='productId'
						label={'Product Type'}
						labelCol={{ span: 24 }}
						rules={[{ ...requiredValidation }]}
					>
						<Select showSearch allowClear getPopupContainer={triggerNode => triggerNode.parentElement}
							onClear={() => clearFields()}
							onSelect={(billType) ? (value: number) => onProductTypeChange(value) : undefined}
							filterOption={(input, option) =>
								(option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
							}
						>
							{
								productListMemo?.map((product) => (
									<React.Fragment key={product?.id}>
										<Select.Option value={product?.id} >{product?.value}</Select.Option>
									</React.Fragment>
								))
							}
						</Select>
					</Form.Item>
				</Col>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						name="subProductId"
						label={'Sub Product Type'}
						labelCol={{ span: 24 }}
						rules={[{ ...requiredValidation }]}
					>
						<Select getPopupContainer={triggerNode => triggerNode.parentElement}
							onSelect={(billType) ? (value: number) => onSubProductTypeChange(value, subProductListMemo) : undefined}
							showSearch
							allowClear

							filterOption={(input, option) =>
								(option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
							}
						>
							{
								subProductListMemo?.map((subProduct) => (
									<React.Fragment key={subProduct?.id}>
										<Select.Option value={subProduct?.id}  >{subProduct?.value}</Select.Option>
									</React.Fragment>
								))
							}
						</Select>
					</Form.Item>
				</Col>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						name="temperatureId"
						label={'Temperature'}
						labelCol={{ span: 24 }}
						rules={[{ ...requiredValidation }]}

					>
						<Select showSearch getPopupContainer={triggerNode => triggerNode.parentElement}
							allowClear
							onClear={onClearTemperatureHandler}
							onChange={onTemperatureChangeHandler}
							filterOption={(input, option) =>
								(option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
							}
						>
							{
								temperatureListMemo?.map((temperature) => (
									<React.Fragment key={temperature?.id}>
										<Select.Option value={temperature?.id} >{temperature?.value}</Select.Option>
									</React.Fragment>
								))
							}
						</Select>
					</Form.Item>
				</Col>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						rules={[{ ...requiredValidation }]}
						name='originCityId'
						labelCol={{ span: 24 }}
						label={'Origin City'}
					>
						<Select
							allowClear getPopupContainer={triggerNode => triggerNode.parentElement}
							loading={countrySearchPromise}
							showSearch
							onChange={(selectedId: number) => onChangeHandler(selectedId, 'originCityId')}
							onClear={() => setFieldValue('originCityId', '')}
							filterOption={(input, option) =>
								(option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
							}
						>
							{
								originListMemo?.map((industry: any) => (
									<React.Fragment key={industry?.id}>
										<Select.Option value={industry?.id}>{industry?.value}</Select.Option>
									</React.Fragment>
								))
							}
						</Select>
					</Form.Item>
				</Col>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						name='originOdaCityId'
						labelCol={{ span: 24 }}
						label={'Origin City ODA'}
					>
						<Select getPopupContainer={triggerNode => triggerNode.parentElement}
							showSearch
							size="middle"
							allowClear
							loading={originCityFetchProgress}
							filterOption={(input, option) =>
								(option!.children as unknown as string)
									?.toLocaleLowerCase()
									?.includes(input?.toLocaleLowerCase())
							}
						>
							{subCityListMemo?.map((subCity) => (
								<React.Fragment key={subCity?.id}>
									<Select.Option value={subCity?.id}>
										{subCity?.value}
									</Select.Option>
								</React.Fragment>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						rules={[{ ...requiredValidation }]}
						name='destinationCityId'
						labelCol={{ span: 24 }}
						label={'Destination City'}
					>
						<Select size="middle" showSearch loading={countrySearchPromise} getPopupContainer={triggerNode => triggerNode.parentElement}
							onChange={(selectedId: number) => onChangeHandler(selectedId, 'destinationCityId')}

							filterOption={(input, option) =>
								(option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
							}>
							{
								destinationListMemo?.map((origin: any) => (
									<React.Fragment key={origin?.id}>
										<Select.Option value={origin?.id} >{origin?.value}</Select.Option>
									</React.Fragment>
								))
							}
						</Select>
					</Form.Item>
				</Col>
				<Col xxl={8} xl={8} lg={6} md={6}>
					<Form.Item
						name='destinationOdaCityId'
						labelCol={{ span: 24 }}
						label={'Destination City ODA'}
					>
						<Select getPopupContainer={triggerNode => triggerNode.parentElement}
							size="middle"
							allowClear
							showSearch
							loading={destinationFetchFetchProgress}
							filterOption={(input, option) =>
								(option!.children as unknown as string)
									?.toLocaleLowerCase()
									?.includes(input?.toLocaleLowerCase())
							}
						>
							{destinationSubCityListMemo?.map((country: any) => (
								<React.Fragment key={country?.id}>
									<Select.Option value={country?.id}>
										{country?.value}
									</Select.Option>
								</React.Fragment>
							))}
						</Select>
					</Form.Item>
				</Col>
			</Row>
		</>
	)
}
function EditComponent({ isOpen, setIsOpen }) {

	const { promiseInProgress: updateManifestArea } = usePromiseTracker({ area: 'update-manifest-area' })
	const { waybillNo } = useManifestContextHook();
	const [form] = Form.useForm();
	const formRef: React.MutableRefObject<any> = useRef(null)
	const GetProductSubProductTemperatureDetailsAction = useBookingStore((state: IBookingState) => state?.GetProductSubProductTemperatureDetailsAction);
	const GetConsigneeDetailsByShipperAndAccountDetailsAction = useBookingStore(state => state?.GetConsigneeDetailsByShipperAndAccountDetailsAction);
	const GetOriginDestinationCountryCityAction = useBookingStore(state => state?.GetOriginDestinationCountryCityAction);
	const GetSubCityListAction = useCommonStore((state: ICommonState) => state?.GetSubCityListAction);
	const GetConsigneeSubCityListAction = useCommonStore((state: ICommonState) => state?.GetConsigneeSubCityListAction);
	const manifestForm = Form.useFormInstance();
	const { getFieldValue } = manifestForm;
	const { reValidateHandle } = RevalidateHook();
	const { employeeDetail } = useUserContextHook() || {};
	const UpdateManifestAction = useOperationStore((state) => state?.UpdateManifestAction);
	const GetOriginListAction = useCommonStore((state) => state?.GetOriginListAction)
	const clientId = getFieldValue('clientId')
	const shipperId = getFieldValue('shipperId')
	const consigneeId = getFieldValue('consigneeId')
	const accountDetailList = useBookingStore((state: IBookingState) => state.accountDetailList);
	const manifestationList = useOperationStore((state) => state?.manifestationList);

	useEffect(() => {
		if (clientId) {
			trackPromise(GetProductSubProductTemperatureDetailsAction(clientId), 'fetch-area');
			//TODO: Commented due to new logic of display country based on the product,temperature and sub-product
			// trackPromise(GetOriginListAction(getFieldValue('destinationCountryId'), 'destination'), 'destination-city-area')
			// trackPromise(GetOriginListAction(getFieldValue('originCountryId'), 'origin'), 'origin-city-area')
		}
	}, [GetOriginListAction, GetProductSubProductTemperatureDetailsAction, clientId])

	const onFinishHandler = useCallback(async (values: IUpdateManifest) => {
		const request: Partial<IUpdateManifest> = {
			clientId: clientId,
			waybillNo: waybillNo,
			shipperId: values?.originCityId ? shipperId : null,
			consigneeId: values?.destinationCityId ? consigneeId : null,
			originCityId: values?.originCityId,
			originIsoda: Boolean(values?.originOdaCityId),
			destinationIsoda: Boolean(values?.destinationOdaCityId),
			originOdaCityId: values?.originOdaCityId,
			destinationOdaCityId: values?.destinationOdaCityId,
			destinationCityId: values?.destinationCityId,
			productId: values?.productId,
			subProductId: values?.subProductId,
			temperatureId: values?.temperatureId
		};

		const isSubsetEqual = (obj1, obj2): boolean => {
			return Object.keys(obj1).every(
				(key) => obj1[key as keyof Partial<IUpdateManifest>] === obj2[key as keyof Partial<IUpdateManifest>]
			);
		};

		if (isSubsetEqual(request, manifestationList)) {
			warningNotificationStyle('No changes detected');
			return;
		}

		const response = await trackPromise(UpdateManifestAction(waybillNo, request), 'update-manifest-area');
		if (response?.status === TWO_HUNDRED_SUCCESS_CODE) {
			setIsOpen(false);
			reValidateHandle(manifestForm);
		}
	}, [UpdateManifestAction, clientId, consigneeId, manifestForm, manifestationList, reValidateHandle, setIsOpen, shipperId, waybillNo])

	const onCancelHandler = useCallback(() => {
		setIsOpen(false);
		form?.setFieldsValue({ ...initialValue });
		clearStore();
	}, [form, setIsOpen])


	const setInitialHandler = (selectedId: number, key: string) => {
		if (!selectedId) {
			return
		}
		if (['originCityId'].includes(key)) {
			const requestBody = ({
				subclientId: clientId,
				productId: +manifestationList?.productId!,
				subProductId: +manifestationList?.subProductId!,
				temperatureId: +manifestationList?.temperatureId!
			});
			const request: IShipperAndAccountDetailRequest = { ...requestBody, originCityId: selectedId }
			trackPromise(GetConsigneeDetailsByShipperAndAccountDetailsAction(request, employeeDetail?.countryId), 'get-consignee-detail');
			trackPromise(GetSubCityListAction(selectedId), 'origin-city-fetch-area');
		}
		if (['destinationCityId'].includes(key)) {
			trackPromise(GetConsigneeSubCityListAction(selectedId), 'destination-fetch-area');
		}
	}


	useEffect(() => {
		if (!accountDetailList || accountDetailList.length === 0) return;

		form?.setFieldsValue({
			productId: manifestationList?.productId,
			subProductId: manifestationList?.subProductId,
			temperatureId: manifestationList?.temperatureId,
		});

		const { subProductList, temperatureList } = getProductDetails(accountDetailList, manifestationList?.productId, manifestationList?.subProductId);
		useBookingStore?.setState({ subProductList, temperatureList });

		const request: IOriginDestinationRequest = {
			subclientId: clientId,
			productId: manifestationList?.productId,
			subProductId: manifestationList?.subProductId,
			temperatureId: +manifestationList?.temperatureId!,
		};

		trackPromise(GetOriginDestinationCountryCityAction(request), 'search-country-area');

		if (manifestationList?.originCityId) {
			setInitialHandler(+manifestationList?.originCityId, 'originCityId');
		}

		if (manifestationList?.destinationCityId) {
			setInitialHandler(+manifestationList?.destinationCityId, 'destinationCityId');
		}
		//eslint-disable-next-line
	}, [accountDetailList]);

	return (
		<Modal okText='Update' open={isOpen} width={800} onOk={form?.submit} onCancel={onCancelHandler}
			title='Edit Account Details' maskClosable={false} confirmLoading={updateManifestArea} >
			<LoadingModalComponent isLoading={updateManifestArea}>
				<Form form={form} initialValues={initialValue} ref={formRef} onFinish={onFinishHandler} autoComplete='off'>
					<EditRowComponent clientId={clientId} />
				</Form>
			</LoadingModalComponent>
		</Modal>

	)
}

export default EditComponent